import { mixed, object, string } from 'yup';

import { buildRichTextSchema } from '../../../../common';
import {
  TREATMENT_ASSESSMENT_MAX_HOURS,
  TREATMENT_DURATION_MAX_MONTHS,
  TREATMENT_DURATION_MIN_MONTHS,
  TREATMENT_RICH_TEXT_MAX_LENGTH,
  TREATMENT_SERVICE_MAX_HOURS,
} from './constants';

import type { Moment } from 'moment';

function buildServiceHoursSchema(maxHours: number) {
  return string()
    .matches(/^\d+$/, { message: 'Enter a whole number', excludeEmptyString: true })
    .test(
      'max-hours',
      `Enter a number from 0 to ${maxHours}`,
      (value) => !value || Number(value) <= maxHours,
    );
}

function buildAssessmentHoursSchema() {
  return buildServiceHoursSchema(TREATMENT_ASSESSMENT_MAX_HOURS).test(
    'combined-assessment-hours',
    `The hours of 97151 and 97152 together must be ${TREATMENT_ASSESSMENT_MAX_HOURS} or fewer`,
    (_value, context) =>
      Number(context.parent.assessment_97151_hours || 0) +
        Number(context.parent.assessment_97152_hours || 0) <=
      TREATMENT_ASSESSMENT_MAX_HOURS,
  );
}

function buildServicePerSchema() {
  return string().required('Select how often');
}

export const TREATMENT_FORM_SCHEMA = object({
  recommendations_explanation: buildRichTextSchema({
    minLength: 1,
    maxLength: TREATMENT_RICH_TEXT_MAX_LENGTH,
    minMessage: 'Required',
    maxMessage: `Enter no more than ${TREATMENT_RICH_TEXT_MAX_LENGTH} characters`,
  }),
  proposed_start_date: mixed<Moment>()
    .nullable()
    .test('valid-date', 'Enter a valid date', (value) => value == null || value.isValid())
    .required('Required'),
  duration_in_months: string()
    .required('Required')
    .matches(/^\d+$/, 'Enter a whole number')
    .test(
      'duration-range',
      `Enter a number from ${TREATMENT_DURATION_MIN_MONTHS} to ${TREATMENT_DURATION_MAX_MONTHS}`,
      (value) =>
        Number(value) >= TREATMENT_DURATION_MIN_MONTHS &&
        Number(value) <= TREATMENT_DURATION_MAX_MONTHS,
    ),
  assessment_97151_hours: buildAssessmentHoursSchema(),
  assessment_97151_per: buildServicePerSchema(),
  assessment_97152_hours: buildAssessmentHoursSchema(),
  assessment_97152_per: buildServicePerSchema(),
  direct_therapy_97153_hours: buildServiceHoursSchema(TREATMENT_SERVICE_MAX_HOURS),
  direct_therapy_97153_per: buildServicePerSchema(),
  protocol_modification_97155_hours: buildServiceHoursSchema(TREATMENT_SERVICE_MAX_HOURS),
  protocol_modification_97155_per: buildServicePerSchema(),
  parent_training_97156_hours: buildServiceHoursSchema(TREATMENT_SERVICE_MAX_HOURS),
  parent_training_97156_per: buildServicePerSchema(),
  group_parent_training_97157_hours: buildServiceHoursSchema(TREATMENT_SERVICE_MAX_HOURS),
  group_parent_training_97157_per: buildServicePerSchema(),
  care_coordination_99366_hours: buildServiceHoursSchema(TREATMENT_SERVICE_MAX_HOURS),
  care_coordination_99366_per: buildServicePerSchema(),
});
